// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { array, bool, func, object, string } from 'prop-types';
import {
  Button,
  Dropdown,
  Loading,
  TextInput
} from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  Download,
  CalendarClock,
  Copy,
  LockKeyhole,
  Package,
  RefreshCw,
  Save,
  ShoppingCart,
  Trash
} from 'lucide-react';

import Img from 'Containers/Image/Image';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import { StyledButton as OrderProductButton } from 'Components/Order/OrderProducts';
import Badges from 'Components/Badges/Badges';
import { OFFER_PRODUCT_STATUS_TRANSLATIONS } from 'Utils/offers';

const StyledEditor = styled.main`
  color: ${ props => props.theme.colors.darkGray };
  padding-bottom: 2rem;

  .editor-card {
    width: 100%;
  }

  .pageTitle {
    align-items: center;
    border-bottom: 2px solid ${ props => props.theme.products.colors.border };
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    min-height: 48px;

    h3 {
      margin: 0;
    }

    .title {
      align-items: center;
      display: flex;
      gap: 0.75rem;
    }
  }

  .status {
    background: #fff7d6;
    border: 1px solid #e5c85b;
    border-radius: 999px;
    color: #7a6200;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;

    &.expired {
      background: ${ props => props.theme.colors.lightRed };
      border-color: ${ props => props.theme.colors.red };
      color: ${ props => props.theme.colors.red };
    }
  }

  .source {
    color: ${ props => props.theme.colors.gray };
    font-size: 0.8rem;
  }

  .storage-banner {
    align-items: flex-start;
    background: #edf8f5;
    border: 1px solid #73b5a5;
    border-radius: 6px;
    display: flex;
    gap: 0.875rem;
    margin: 1rem 0;
    padding: 0.875rem 1rem;

    strong {
      color: #164f43;
    }

    &.expired-banner {
      align-items: center;
      background: ${ props => props.theme.colors.lightRed };
      border-color: ${ props => props.theme.colors.red };
      color: ${ props => props.theme.colors.red };

      p {
        flex: 1;
      }

      strong {
        color: ${ props => props.theme.colors.red };
      }

      button {
        background: transparent;
        flex-shrink: 0;
        gap: 0.5rem;

        &:hover,
        &:focus,
        &:active {
          background: transparent;
        }
      }
    }
  }

  .metadata {
    border: 1px solid ${ props => props.theme.products.colors.border };
    border-radius: 6px;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 1rem 0;
    padding: 1rem;

    .client {
      grid-column: span 2;
    }

    input,
    .cds--dropdown {
      background: ${ props => props.theme.colors.white };
      border: 1px solid ${ props => props.theme.products.colors.border };
      border-radius: 4px;
    }

    .helper {
      color: ${ props => props.theme.colors.gray };
      font-size: 0.7rem;
      margin-top: 0.35rem;
    }
  }

  .product-tools {
    align-items: flex-end;
    display: flex;
    justify-content: space-between;
    margin: 1rem 0;

    button {
      gap: 0.5rem;
      justify-content: center;
    }

    .price-refresh-control {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
    }

    .price-refresh-hint {
      color: ${ props => props.theme.colors.gray };
      font-size: 0.75rem;
      margin-top: 0.25rem;
      text-align: right;
    }

    .refresh-icon.loading {
      animation: offer-refresh-spin 0.8s linear infinite;
    }
  }

  @keyframes offer-refresh-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    border-collapse: collapse;
    min-width: 1050px;
    width: 100%;
  }

  th {
    background: #f5f7f9;
    color: #3a3a3a;
    font-size: 16px;
    font-weight: 500;
    padding: 0.75rem;
    text-align: left;
    vertical-align: middle;
  }

  td {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
    padding: 0.875rem 0.75rem;
    vertical-align: middle;
  }

  tr.product-error > td,
  tr.product-error-message > td {
    background-color: ${ props => props.theme.colors.lightRed } !important;
  }

  tr.product-error > td {
    border-bottom: 0;
  }

  tr.product-error-message > td {
    color: ${ props => props.theme.colors.red };
    font-size: 0.875rem;
    font-weight: 500;
    padding-top: 0;
  }

  .stock-value.out-of-stock {
    color: ${ props => props.theme.colors.red };
  }

  .product-name {
    font-weight: 600;
    max-width: 260px;
  }

  .product-link {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .product-title {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    line-height: 1.3;
    max-height: 2.6em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-info {
    align-items: center;
    display: flex;
    gap: 0.75rem;

    > div {
      min-width: 0;
    }

    .badges {
      justify-content: flex-start;
      margin: 2px 0 0;

      .badge {
        font-size: 12px;
        min-height: auto;
      }
    }
  }

  .product-thumb {
    align-items: center;
    background: ${ props => props.theme.colors.altGray };
    border-radius: 5px;
    display: flex;
    flex: 0 0 54px;
    height: 54px;
    justify-content: center;
    overflow: hidden;

    .image {
      max-height: 100%;
      max-width: 100%;
    }
  }

  .unit {
    color: ${ props => props.theme.colors.gray };
    display: block;
    font-size: 0.7rem;
    margin-top: 0.25rem;
  }

  .sku {
    align-items: center;
    color: ${ props => props.theme.colors.darkGray };
    display: flex;
    font-size: 0.875rem;
    margin: 5px 0;

    .sku-label {
      font-weight: 600;
    }

    .sku-value {
      font-weight: normal;
      margin: 0 5px;
    }

    button {
      background: none;
      border: 0;
      color: ${ props => props.theme.colors.altOrange };
      cursor: pointer;
      display: inline-flex;
      padding: 0;
    }
  }

  .numeric-input {
    background: white;
    border: 1px solid ${ props => props.theme.products.colors.border };
    border-radius: 4px;
    min-height: 2.25rem;
    padding: 0.375rem 0.5rem;
    width: 78px;
  }

  .quantitySelector {
    height: 35px;
    width: 90px;

    button.quantitySelect,
    input {
      height: 35px;
      max-height: 35px;
      min-height: 35px;
    }

    button.quantitySelect {
      margin: 0;
      min-width: 25px;
      padding: 0;
    }
  }

  .inline-unit-input {
    align-items: center;
    display: flex;
    gap: 0.35rem;

    .unit {
      margin-top: 0;
    }
  }

  .price-with-change {
    align-items: flex-start;
    display: inline-flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .price-current {
    align-items: center;
    display: inline-flex;
    gap: 0.35rem;
  }

  .price-old {
    color: ${ props => props.theme.colors.gray };
    font-weight: 400;
  }

  .price-change-indicator {
    align-items: center;
    display: inline-flex;

    &.increase {
      color: ${ props => props.theme.colors.red };
    }

    &.decrease {
      color: ${ props => props.theme.colors.green };
    }

    &.price-pending {
      font-size: 0.8rem;
      gap: 0.2rem;
    }
  }

  .confidential {
    background: #edf8f5;
    border: 1px solid #73b5a5;
    border-radius: 999px;
    color: #236658;
    display: block;
    font-size: 0.6rem;
    margin-top: 0.25rem;
    padding: 0.1rem 0.35rem;
    width: max-content;
  }

  .delete-product {
    background: #fef2e8;
    border: 1px solid #dde6f2;
    color: #3a3a3a;
    min-height: auto;
    padding: 7px;

    &:hover {
      background: #fbb2ac;
    }
  }

  .empty {
    color: ${ props => props.theme.colors.gray };
    padding: 2rem;
    text-align: center;
  }

  .totals {
    display: flex;
    justify-content: flex-end;
    margin: 1rem 0;

    > div {
      border-left: 1px solid ${ props => props.theme.products.colors.border };
      min-width: 130px;
      padding: 0.75rem 1rem;
      text-align: right;
    }

    .grand-total {
      background: #edf3ff;
      color: ${ props => props.theme.colors.blue };

      strong {
        color: ${ props => props.theme.colors.darkGray };
        font-size: 1.25rem;
      }
    }

    small {
      color: ${ props => props.theme.colors.gray };
      display: block;
      margin-bottom: 0.35rem;
    }
  }

  .actions {
    border-top: 1px solid ${ props => props.theme.products.colors.border };
    display: grid;
    gap: 1rem;
    grid-template-columns: 1.2fr 1fr 1.2fr;
    padding: 1rem 0;

    button {
      gap: 0.5rem;
      justify-content: center;
      max-width: none;
      min-height: 3rem;
      padding: 0 1rem;
      width: 100%;
    }

    .save {
      border: 1px solid ${ props => props.theme.colors.blue };
      color: ${ props => props.theme.colors.blue };
    }

    .cart {
      background: ${ props => props.theme.colors.red };
    }

    .export {
      border: 1px solid ${ props => props.theme.colors.blue };
      color: ${ props => props.theme.colors.blue };
    }

    .export-control {
      display: flex;

      .export {
        flex: 1;
      }

      .cds--dropdown__wrapper {
        flex: 0 0 120px;
      }

      .cds--dropdown {
        background: ${ props => props.theme.colors.white };
        border: 1px solid ${ props => props.theme.colors.blue };
        border-left: 0;
        color: ${ props => props.theme.colors.blue };
        min-height: 3rem;
      }

      .cds--label {
        display: none;
      }
    }

    p {
      color: ${ props => props.theme.colors.gray };
      font-size: 0.7rem;
      line-height: 1.35;
      margin-top: 0.4rem;
    }
  }

  @media screen and (max-width: 1024px) {
    .metadata {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .actions {
      grid-template-columns: 1fr;
    }
  }

  @media screen and (max-width: 600px) {
    .pageTitle {
      align-items: flex-start;
      flex-direction: column;
      gap: 0.5rem;
    }

    .metadata {
      grid-template-columns: 1fr;

      .client {
        grid-column: span 1;
      }
    }

    .product-tools {
      align-items: stretch;
      flex-direction: column;
      gap: 0.75rem;

      .price-refresh-control {
        align-items: stretch;
      }

      .price-refresh-hint {
        text-align: left;
      }
    }

    .expired-banner {
      align-items: flex-start;
      flex-wrap: wrap;

      p {
        flex-basis: calc(100% - 45px);
      }

      button {
        margin-left: 39px;
      }
    }

    .totals {
      flex-direction: column;

      > div {
        border-bottom: 1px solid ${ props => props.theme.products.colors.border };
      }
    }
  }
`;

const OfferEditor = ({
  offer,
  products,
  currencies,
  exportLoading,
  saveLoading,
  isSaving,
  pricesLoading,
  priceRefreshDisabled,
  renewLoading,
  cartLoading,
  exportFormat,
  exportFormats,
  isExpired,
  isSavedOffer,
  productErrors = [],
  validUntil,
  minimumValidUntil,
  totals,
  formatMoney,
  validators,
  register,
  rhfErrors,
  onOfferChange,
  onValidUntilChange,
  onCurrencyChange,
  onGlobalMarkupChange,
  onOpenProductModal,
  onRefreshPrices,
  onRenew,
  onProductChange,
  onRemoveProduct,
  onCopySku,
  onSave,
  onAddToCart,
  onExportFormatChange,
  onExport
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sharedProps = fieldName => ({
    id: `offerEditor${ fieldName }`,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, {
      ...validators[fieldName],
      onChange: onOfferChange
    })
  });
  let statusTranslationKey = isSavedOffer ? 'offers.savedStatus' : 'offers.unsavedOffer';
  if (isExpired) statusTranslationKey = 'offers.expiredOffer';
  const hasProductErrors = products.some(product => {
    const productId = product.productId || product.id;
    return productErrors.some(
      error => String(error.productId).trim().toLowerCase()
        === String(productId).trim().toLowerCase()
    );
  });
  const refreshPricesDisabled = isExpired
    || pricesLoading
    || priceRefreshDisabled
    || !products.length;

  return (
    <StyledEditor className="grid-container" theme={ theme }>
      { isSaving && <Loading active withOverlay /> }
      <div className="editor-card">
        <header className="pageTitle">
          <div className="title">
            <h3>{ t('offers.editorTitle') }</h3>
            <span className={ `status ${ isExpired ? 'expired' : '' }` }>
              { t(statusTranslationKey) }
            </span>
          </div>
          { offer.sourceWishlist?.name && (
            <p className="source">
              { t('offers.startedFromWishlist', { name: offer.sourceWishlist.name }) }
            </p>
          ) }
        </header>

        { !isSavedOffer && (
          <div className="storage-banner">
            <LockKeyhole size={ 25 } />
            <p dangerouslySetInnerHTML={ { __html: t('offers.editorStorageBanner') } } />
          </div>
        ) }

        { isExpired && (
          <div className="storage-banner expired-banner">
            <LockKeyhole size={ 25 } />
            <p><strong>{ t('offers.expiredReadOnly') }</strong></p>
            <Button
              kind="ghost"
              type="button"
              disabled={ renewLoading }
              onClick={ onRenew }
            >
              <CalendarClock size={ 17 } />
              { renewLoading ? t('offers.renewing') : t('offers.renewOffer') }
            </Button>
          </div>
        ) }

        <section className="metadata">
          <TextInput
            required
            labelText={ t('offers.offerName') }
            disabled={ isExpired }
            { ...sharedProps('name') }
          />
          <TextInput
            id="offerEditorValidity"
            type="date"
            required
            min={ minimumValidUntil }
            labelText={ t('offers.validity') }
            value={ validUntil }
            disabled={ isExpired }
            onChange={ onValidUntilChange }
          />
          <div>
            <Dropdown
              id="offerEditorCurrency"
              titleText={ t('offers.currency') }
              label={ t('offers.currency') }
              items={ currencies }
              selectedItem={ currencies.find(
                item => item.id === String(offer.currencyId || 'ron').toLowerCase()
              ) || currencies.at(0) }
              disabled={ isExpired }
              onChange={ onCurrencyChange }
            />
          </div>
          <div>
            <TextInput
              id="offerEditorGlobalMarkup"
              name="defaultMarkupPercent"
              type="number"
              min="0"
              max="10000"
              labelText={ t('offers.globalMarkup') }
              value={ offer.defaultMarkupPercent }
              disabled={ isExpired }
              onChange={ onGlobalMarkupChange }
            />
          </div>
          <TextInput
            className="client"
            labelText={ t('offers.client') }
            placeholder={ t('offers.clientPlaceholder') }
            disabled={ isExpired }
            { ...sharedProps('clientName') }
          />
          <TextInput
            className="client"
            type="email"
            labelText={ t('offers.clientEmail') }
            placeholder={ t('offers.clientPlaceholder') }
            disabled={ isExpired }
            { ...sharedProps('clientEmail') }
          />
        </section>

        <section className="product-tools">
          <OrderProductButton
            kind="ghost"
            type="button"
            theme={ theme }
            disabled={ isExpired }
            onClick={ onOpenProductModal }
          >
            + { t('offers.addProduct') }
          </OrderProductButton>
          <div className="price-refresh-control">
            <Button
              kind="ghost"
              type="button"
              disabled={ refreshPricesDisabled }
              onClick={ onRefreshPrices }
            >
              <RefreshCw
                className={ `refresh-icon ${ pricesLoading ? 'loading' : '' }` }
                size={ 17 }
              />
              { t('offers.refreshNodPrices') }
            </Button>
            { !refreshPricesDisabled && (
              <small className="price-refresh-hint">{ t('offers.refreshPricesHint') }</small>
            ) }
          </div>
        </section>

        <div className="table-wrap">
          { products.length ? (
            <table>
              <thead>
                <tr>
                  <th>{ t('offers.product') }</th>
                  <th>{ t('offers.editorStock') }</th>
                  <th>{ t('offers.quantity') }</th>
                  <th>{ t('offers.editorPrice') }</th>
                  <th>{ t('offers.productMarkup') }<span className="confidential">{ t('offers.confidential') }</span></th>
                  <th>{ t('offers.orCustomPrice') }<span className="confidential">{ t('offers.confidential') }</span></th>
                  <th>{ t('offers.offeredPrice') }<span className="confidential">{ t('offers.confidential') }</span></th>
                  <th>{ t('offers.total') }</th>
                  <th>{ t('offers.actions') }</th>
                </tr>
              </thead>
              <tbody>
                { products.map(product => {
                  const productId = product.productId || product.id;
                  const productError = productErrors.find(
                    error => String(error.productId).trim().toLowerCase()
                      === String(productId).trim().toLowerCase()
                  );
                  return (
                    <React.Fragment key={ productId }>
                      <tr className={ productError ? 'product-error' : '' }>
                        <td className="product-name">
                          <div className="product-info">
                            <Link
                              className="product-thumb"
                              to={ `/products/${ product.slug || 'slug' }/pd/${ product.productId || product.id }` }
                              state={ {
                                component: 'offer_editor',
                                source: 'offer_editor_page',
                                clickedOn: 'image',
                                productId: product.productId || product.id
                              } }
                            >
                              <Img
                                className="image"
                                src={ product.pictureUrl }
                                unloader={ <Package size={ 28 } strokeWidth={ 1.25 } /> }
                                alt={ product.title || product.name || '' }
                              />
                            </Link>
                            <div>
                              <Link
                                className="product-link product-title"
                                to={ `/products/${ product.slug || 'slug' }/pd/${ product.productId || product.id }` }
                                state={ {
                                  component: 'offer_editor',
                                  source: 'offer_editor_page',
                                  clickedOn: 'title',
                                  productId: product.productId || product.id
                                } }
                              >
                                { product.title || product.name }
                              </Link>
                              <span className="sku">
                                <span className="sku-label">{ t('products.code') }:</span>
                                <span className="sku-value">{ product.code }</span>
                                <button
                                  type="button"
                                  aria-label={ t('offers.copySku') }
                                  onClick={ () => onCopySku(product.code) }
                                >
                                  <Copy size={ 18 } />
                                </button>
                              </span>
                              { (isSavedOffer ? !!product?.supplierId : !product?.hasPrice) && (
                                <Badges isSupplierOffer />
                              ) }
                            </div>
                          </div>
                        </td>
                        <td
                          className={ `stock-value ${
                            Number(product.stockValue ?? product.stock) === 0 ? 'out-of-stock' : ''
                          }` }
                        >
                          { product.stockValue ?? product.stock ?? '-' }
                        </td>
                        <td>
                          <QuantitySelect
                            size="md"
                            value={ product.quantity }
                            min={ Math.max(Number(product.minQuantity || 1), 1) }
                            step={ Math.max(Number(product.minQuantity || 1), 1) }
                            max={ Math.max(
                              100,
                              Number(product.stockValue ?? product.stock ?? 0)
                            ) }
                            disabled={ isExpired }
                            onChange={ value => onProductChange(product, 'quantity', value) }
                          />
                        </td>
                        <td>
                          { product.nodPriceChange && !product.nodPriceChange.isApplied ? (
                            <span className="price-with-change">
                              <span className="price-old">
                                { formatMoney(product.nodPriceChange.old) }
                              </span>
                              <span className="price-current">
                                <strong>{ formatMoney(product.nodPriceChange.new) }</strong>
                                <span
                                  className={ `price-change-indicator ${
                                    product.nodPriceChange.new > product.nodPriceChange.old
                                      ? 'increase'
                                      : 'decrease'
                                  }` }
                                  title={
                                    `${ formatMoney(product.nodPriceChange.old) } -> ${
                                      formatMoney(product.nodPriceChange.new)
                                    }`
                                  }
                                >
                                  { product.nodPriceChange.new > product.nodPriceChange.old
                                    ? <ArrowUp size={ 16 } strokeWidth={ 2.5 } />
                                    : <ArrowDown size={ 16 } strokeWidth={ 2.5 } /> }
                                </span>
                              </span>
                            </span>
                          ) : <strong>{ formatMoney(product.nodPrice) }</strong> }
                        </td>
                        <td>
                          <div className="inline-unit-input">
                            <input
                              aria-label={ t('offers.productMarkup') }
                              className="numeric-input"
                              type="number"
                              min="0"
                              max="10000"
                              placeholder={ `${ offer.defaultMarkupPercent }%` }
                              value={ product.markupPercent ?? '' }
                              disabled={ isExpired
                                || (product.unitCustomPrice !== null
                                  && product.unitCustomPrice !== undefined) }
                              onChange={ event => onProductChange(product, 'markupPercent', event.target.value) }
                            />
                            <span className="unit">%</span>
                          </div>
                        </td>
                        <td>
                          <input
                            aria-label={ t('offers.orCustomPrice') }
                            className="numeric-input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={ product.unitCustomPriceDisplay ?? '' }
                            disabled={ isExpired }
                            onChange={ event => onProductChange(product, 'unitCustomPrice', event.target.value) }
                          />
                        </td>
                        <td><strong>{ formatMoney(product.unitOfferPrice) }</strong></td>
                        <td><strong>{ formatMoney(product.lineTotal) }</strong></td>
                        <td>
                          <Button
                            className="delete-product"
                            kind="ghost"
                            iconDescription={ t('common.delete') }
                            disabled={ isExpired && !productError }
                            onClick={ () => onRemoveProduct(product) }
                          >
                            <Trash size={ 18 } />
                          </Button>
                        </td>
                      </tr>
                      { productError && (
                        <tr className="product-error-message">
                          <td colSpan={ 9 }>
                            { t(
                              OFFER_PRODUCT_STATUS_TRANSLATIONS[productError.status]
                              || 'offers.cartStatusProductUnavailable'
                            ) }
                          </td>
                        </tr>
                      ) }
                    </React.Fragment>
                  );
                }) }
              </tbody>
            </table>
          ) : <p className="empty">{ t('offers.emptyOffer') }</p> }
        </div>

        <section className="totals">
          <div><small>{ t('offers.subtotal') }</small><strong>{ formatMoney(totals.subtotal) }</strong></div>
          <div><small>{ t('offers.vat') }</small><strong>{ formatMoney(totals.vat) }</strong></div>
          <div className="grand-total"><small>{ t('offers.offerTotal') }</small><strong>{ formatMoney(totals.total) }</strong></div>
        </section>

        <footer className="actions">
          <div>
            <Button
              className="save"
              kind="ghost"
              disabled={ isSavedOffer || isExpired || saveLoading || !products.length }
              onClick={ onSave }
            >
              <Save size={ 18 } /> { t(saveLoading ? 'offers.saving' : 'offers.saveForLater') }
            </Button>
            <p>{ t('offers.savePrivacyMessage') }</p>
          </div>
          <div>
            <Button
              className="cart"
              kind="danger"
              disabled={ cartLoading || !products.length || hasProductErrors }
              onClick={ onAddToCart }
            >
              { cartLoading && <Loading active small withOverlay={ false } /> }
              <ShoppingCart size={ 18 } /> { t('offers.moveToCart') }
            </Button>
            <p>{ t('offers.moveToCartMessage') }</p>
          </div>
          <div>
            <div className="export-control">
              <Button
                className="export"
                kind="ghost"
                disabled={ exportLoading || !products.length }
                onClick={ onExport }
              >
                <Download size={ 18 } /> { t('offers.exportOffer') }
              </Button>
              <Dropdown
                id="offerExportFormat"
                titleText={ t('offers.exportFormat') }
                label={ t('offers.exportFormat') }
                items={ exportFormats }
                selectedItem={ exportFormats.find(item => item.id === exportFormat) }
                disabled={ exportLoading }
                onChange={ onExportFormatChange }
              />
            </div>
            <p>{ t('offers.exportMessage') }</p>
          </div>
        </footer>
      </div>
    </StyledEditor>
  );
};

OfferEditor.propTypes = {
  offer: object,
  products: array,
  currencies: array,
  exportLoading: bool,
  saveLoading: bool,
  isSaving: bool,
  pricesLoading: bool,
  priceRefreshDisabled: bool,
  renewLoading: bool,
  cartLoading: bool,
  exportFormat: string,
  exportFormats: array,
  isExpired: bool,
  isSavedOffer: bool,
  productErrors: array,
  validUntil: string,
  minimumValidUntil: string,
  totals: object,
  formatMoney: func,
  validators: object,
  register: func,
  rhfErrors: object,
  onOfferChange: func,
  onValidUntilChange: func,
  onCurrencyChange: func,
  onGlobalMarkupChange: func,
  onOpenProductModal: func,
  onRefreshPrices: func,
  onRenew: func,
  onProductChange: func,
  onRemoveProduct: func,
  onCopySku: func,
  onSave: func,
  onAddToCart: func,
  onExportFormatChange: func,
  onExport: func
};

export default OfferEditor;
